import { keepPreviousData, useQueries, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { type PaginationState } from "@tanstack/react-table";

const BLOCK_SIZE = 500;

export function useBlockWindowQuery<
  TData extends { data: TRow[]; count: number },
  TQueryKey extends readonly unknown[],
  TParams extends { pagination: PaginationState },
  // Last and defaulted: nothing infers it, so without the default it would be `unknown`.
  TRow = TData["data"][number],
>(
  queryOptionsGetter: (params: TParams) => UseQueryOptions<TData, Error, TData, TQueryKey>,
  params: TParams,
  overrides?: Partial<UseQueryOptions<NoInfer<TData>, Error, NoInfer<TData>, NoInfer<TQueryKey>>>,
) {
  const { pageIndex, pageSize } = params.pagination;
  const blockIndex = Math.floor((pageIndex * pageSize) / BLOCK_SIZE);
  const offsetWithinBlock = (pageIndex * pageSize) % BLOCK_SIZE;

  const queryClient = useQueryClient();
  const currentBlockOpts = queryOptionsGetter({
    ...params,
    pagination: { pageIndex: blockIndex, pageSize: BLOCK_SIZE },
  });
  const cachedCount =
    currentBlockOpts.queryKey ? queryClient.getQueryData<TData>(currentBlockOpts.queryKey)?.count : undefined;

  const makeBlockOptions = (offset: number) => {
    const idx = blockIndex + offset;
    const opts =
      offset === 0 ? currentBlockOpts : (
        queryOptionsGetter({ ...params, pagination: { pageIndex: idx, pageSize: BLOCK_SIZE } })
      );
    return {
      ...opts,
      ...overrides,
      enabled:
        (overrides?.enabled ?? true) &&
        idx >= 0 &&
        (offset === 0 ||
          ((cachedCount === undefined || idx * BLOCK_SIZE < cachedCount) &&
            (offset < 0 ? offsetWithinBlock < 2 * pageSize : offsetWithinBlock >= BLOCK_SIZE - 2 * pageSize))),
      gcTime: 2 * 60 * 1000,
      placeholderData: keepPreviousData,
    };
  };

  return useQueries({
    queries: [makeBlockOptions(-1), makeBlockOptions(0), makeBlockOptions(1)],
    combine: (results) => {
      const current = results[1];
      return {
        ...current,
        data:
          current.data ?
            {
              ...current.data,
              data: current.data.data.slice(offsetWithinBlock, offsetWithinBlock + pageSize),
            }
          : undefined,
      };
    },
  });
}
